// =====================================================================
// bordereaux/auto.js — M-015 bordereaux automatiques : textes de l'onglet « ⏰ Automatique » (27-10-2026)
// Fonctions pures (testées par test/bordereaux-auto.test.mjs). Les RÈGLES (heures décalées, jours, réessai)
// sont appliquées par le serveur (bordereau-auto.rules.js) ; l'écran affiche ce qu'il renvoie.
// =====================================================================

export const JOURS = ['Lu', 'Ma', 'Me', 'Je', 'Ve'];

/** Heure de Bruxelles « HH:MM » d'une date. */
export const heureBxl = (d) => (d ? new Intl.DateTimeFormat('fr-BE', { timeZone: 'Europe/Brussels', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date(d)) : '—');

const pluriel = (n, un, plusieurs) => `${n} ${n > 1 ? plusieurs : un}`;

/** Phrase de résumé d'un réglage (sous la fiche du transporteur). */
export function resumeReglage(t) {
  if (!t.actif) return 'Automatique coupé : génération à la main seulement.';
  const jours = t.jours.length === 5 ? 'jour ouvré' : t.jours.map((j) => JOURS[j]).join(', ');
  return `Chaque ${jours} à ${t.heures.join(' et ')} → bordereau + impression Konica (${t.copies} ex.)`
    + (t.reessai ? ' · réessai +30 min si des colis restent' : '')
    + (t.prochaine ? ` · prochaine : ${t.prochaine}` : ` · plus rien aujourd'hui${t.prochainJour ? ` (prochaine : ${t.prochainJour})` : ''}`);
}

/** Bordereaux par requête d'annulation (le serveur en accepte 8 ; 4 laisse de la marge sous les 180 s, EffiTrace lent compris). */
export const ANNULATION_LOT = 4;
/** Bordereaux par requête de sortie sans signature (= SORTIE_MAX du serveur) : une seule sortie EffiTrace par paquet. */
export const SORTIE_LOT = 300;
/** Ligne « pas annulé » d'un échec : n° du bordereau, sinon ce qu'il désigne (lot non tenté…). */
export const texteEchec = (x) => `${x.numero ? `n° ${x.numero}` : x.carrierName || '—'} (${x.message})`;
export const LIBELLE_MODE = { AUTO: 'auto', REESSAI: 'réessai', TOUS: 'tous', MANUEL: 'manuel', ANNULATION: 'annulé', SORTIE: 'sans sign.' };

/** Texte d'impression d'une exécution : « imprimé ✓ », « 1 en file », « échec : … ». */
export function texteImpression(i) {
  if (!i || !i.total) return '';
  if (i.echecs) return `impression : ${i.echecs} échec${i.echecs > 1 ? 's' : ''}${i.erreur ? ` (${i.erreur})` : ''}`;
  if (i.enFile) return `impression : ${i.faits}/${i.total} sorti${i.faits > 1 ? 's' : ''}, ${i.enFile} en file`;
  return `imprimé${i.total > 1 ? 's' : ''} ✓ Konica`;
}

/** Le message d'un geste à la main SANS sa raison (« … » en tête, texte libre) : le ton ne se juge que sur le reste. */
const horsRaison = (m) => String(m || '').replace(/^«[^»]*»\s*(·\s*)?/, '');
/** { texte, ton: 'ok' | 'warn' | 'crit' | 'neutre' } d'une ligne du journal. */
export function texteJournal(j) {
  if (j.statut === 'EN_COURS') return { texte: 'en cours…', ton: 'neutre' };
  if (j.mode === 'ANNULATION') {
    const n = j.bordereaux || 0;
    return { texte: `${n} bordereau${n > 1 ? 'x' : ''} annulé${n > 1 ? 's' : ''} (${j.colis || 0} colis revenus en attente)${j.message ? ` · ${j.message}` : ''}`,
      ton: j.statut === 'ERREUR' ? 'crit' : /pas annulé/.test(horsRaison(j.message)) ? 'warn' : 'neutre' };
  }
  if (j.mode === 'SORTIE') {
    const n = j.bordereaux || 0;
    return { texte: `${n} bordereau${n > 1 ? 'x' : ''} sorti${n > 1 ? 's' : ''} sans signature (${j.colis || 0} colis)${j.message ? ` · ${j.message}` : ''}`,
      ton: j.statut === 'ERREUR' ? 'crit' : /pas sorti|pas notés/.test(horsRaison(j.message)) ? 'warn' : 'neutre' };
  }
  const reessai = j.reessaiA ? ` · réessai à ${heureBxl(j.reessaiA)}` : '';
  if (j.statut === 'ERREUR') return { texte: `échec : ${j.message || 'erreur inconnue'}${reessai}`, ton: 'crit' };
  if (j.statut === 'RIEN') return { texte: `${j.message || 'rien de contrôlé'} — rien imprimé${reessai}`, ton: 'warn' };
  const morceaux = [`${pluriel(j.bordereaux, 'bordereau', 'bordereaux')} (${pluriel(j.colis, 'colis', 'colis')})`];
  const imp = texteImpression(j.impression);
  if (imp) morceaux.push(`${imp} (${j.copies} ex.)`);
  if (j.restants) morceaux.push(`⚠ ${j.restants} colis non contrôlés restés hors bordereau`);
  if (j.reessaiA) morceaux.push(`réessai à ${heureBxl(j.reessaiA)}`);
  if (j.message) morceaux.push(j.message);
  const ton = j.impression?.echecs ? 'crit' : (j.restants || j.message) ? 'warn' : 'ok';
  return { texte: morceaux.join(' · '), ton };
}

/**
 * Lignes de la liste : un transporteur apparaît s'il a des colis en attente, des bordereaux à signer ou une heure
 * automatique ; les autres (ceux qu'EffiTrace propose mais sans rien en cours) restent accessibles en dessous.
 */
export function lignesListe(carriers, colis, transporteurs) {
  const par = new Map();
  const ajouter = (code, nom) => {
    const c = String(code);
    if (!par.has(c)) par.set(c, { code: c, name: nom, colis: 0, controles: 0, aControler: 0, pending: 0, auto: null });
    return par.get(c);
  };
  for (const c of carriers || []) Object.assign(ajouter(c.code, c.name), { pending: c.pending || 0 });
  for (const l of colis?.lignes || []) Object.assign(ajouter(l.carrierCode, l.carrierName), { colis: l.colis, controles: l.controles, aControler: l.aControler });
  for (const t of transporteurs || []) ajouter(t.carrierCode, t.carrierName).auto = t;
  const toutes = [...par.values()];
  const actives = toutes.filter((l) => l.colis || l.pending || l.auto?.actif)
    .sort((a, b) => b.pending - a.pending || b.colis - a.colis || a.name.localeCompare(b.name));
  const autres = toutes.filter((l) => !actives.includes(l)).sort((a, b) => a.name.localeCompare(b.name));
  return { actives, autres };
}
